/**
 * Shared visual mapping for the aanwezigheidskalender (Epic 4, Story 4.2).
 *
 * Reuses `LEAVE_TYPE_COLORS`/`LEAVE_TYPE_NAMES` from the existing Nmbrs
 * team-verlofkalender legend (Story 3.4) — one color-per-verloftype table,
 * not a second one (AD-9 spirit: no duplicate lookup tables). Spine
 * Consistency Conventions clarify for THIS screen specifically: only
 * `goedgekeurd` (verloftype-kleur-legenda) occurs; "afgewezen"/
 * "ingetrokken" are not applicable here.
 *
 * Epic aanwezigheidscel-iteratie2, Story 1.1 (CAP-4, AC 7): the fixed
 * teal-arcering color constant that used to back `verlof_in_afwachting`'s
 * own render branch is REMOVED (dead code) — the kalender-resolver no
 * longer produces that status, and its remaining defensive case in
 * `statusLabel`/`AanwezigheidCell.HalfCell` renders identically to `werkt`
 * (no separate color needed).
 *
 * Epic aanwezigheid-verlof-vrij, Story 1.1 (CAP-1/CAP-2): goedgekeurd
 * verlof toont voortaan uniform "Vrij" — voor élk verloftype, ongeacht
 * kijker — in plaats van de concrete verloftype-naam. `LEAVE_TYPE_COLORS`/
 * `LEAVE_TYPE_NAMES` blijven hier geïmporteerd/re-exported (o.a. voor de
 * bestaande `aanwezigheidVisuals.pastel.test.ts`-AA-test, die de helper
 * generiek test, niet de cel-rendering), maar de verlof-cel/-legenda in
 * ditzelfde scherm gebruiken ze niet langer om te renderen — die
 * consolideren op de éne `VRIJ_COLOR` hieronder. `leaveColorFor` (het
 * per-type-kleurhelper) is VERWIJDERD — na de consolidatie had niets in de
 * aanwezigheidskalender het nog nodig (review-bevinding 5).
 */

import { LEAVE_TYPE_COLORS, LEAVE_TYPE_NAMES, DEFAULT_LEAVE_COLOR } from '../../nmbrs/components/NmbrsCalendarLegend';
import type { AttendanceDagdeelStatus } from '@functieloonhuis/shared';

export { LEAVE_TYPE_COLORS, LEAVE_TYPE_NAMES, DEFAULT_LEAVE_COLOR };

/**
 * Was de "verlof/afwezig"-kleur voor een peer-kijker (concreet verloftype
 * niet zichtbaar, AD-6 privacy). Epic aanwezigheid-verlof-vrij, Story 1.1
 * (adversariële review): sinds de VRIJ_COLOR-consolidatie hieronder gebruikt
 * de verlof-cel/-legenda deze kleur NIET meer — élke kijker (detail én
 * peer) krijgt nu dezelfde `VRIJ_COLOR`. `GENERIC_LEAVE_COLOR` blijft alleen
 * bestaan als test-fixture in `aanwezigheidVisuals.pastel.test.ts`'s
 * generieke AA-contrast-regressietest (die de `pastelTint`/`readableInk`-
 * helpers test, niet de cel-rendering) — geen productie-consument meer.
 */
export const GENERIC_LEAVE_COLOR = '#94A3B8';

/**
 * Epic aanwezigheid-verlof-vrij, Story 1.1 (CAP-2, review-bevinding 3): de
 * ENE kleur voor élk goedgekeurd-verlof-dagdeel — een eigen, herkenbare
 * zacht-lavendel/violet-hue (niet neutraal grijs, dat zou botsen met
 * weekend-grijs `#F3F4F6` en het lege witte vak). Gemengd via `pastelTint`
 * (geen `color-mix()`-runtime) tot een gedempte pastel-achtergrond, met
 * `readableInk` voor een AA-leesbaar label — zelfde behandeling als elke
 * bestaande `LEAVE_TYPE_COLORS`-kleur.
 */
export const VRIJ_COLOR = '#7C6FCF';

/** Feestdag-kleur — consistent met het bestaande amber-precedent in `PlanningDayCell.tsx`. */
export const HOLIDAY_COLOR = '#B45309';
export const HOLIDAY_BG = '#FFFBEB';
export const HOLIDAY_BORDER = '#FDE68A';

/**
 * Ziek-kleur (Epic nmbrs-verzuim-kalender, Story 1.3) — navy, XXtract-
 * huisstijl. `#D64545` (rood) is UITGESLOTEN: gereserveerd voor echte
 * fouten (tech-spec Constraints). Onderscheidend van elke bestaande
 * dagdeel-kleur/patroon op dit scherm: navy komt vandaag alleen voor als
 * TEKSTkleur op `verlof_in_afwachting` (`text-[#2F5A7A]`), nooit als
 * achtergrond — dit is de eerste solid-navy-achtergrond op het scherm.
 *
 * Epic ux-aanwezigheidscel-duur, Story 1.2 (CAP-2, Open Decision 2):
 * herstyled van solid-navy-achtergrond naar een gedempte, navy-getinte
 * diagonale arcering (zelfde stijl-familie als `niet_werkbaar_patroon`) —
 * `ZIEK_COLOR` blijft bestaan als referentiewaarde (o.a. hergebruikt in
 * `ZIEK_STRIPE_DARK`), maar wordt niet langer als solid `backgroundColor`
 * toegepast.
 */
export const ZIEK_COLOR = '#2F5A7A';

/** Navy-getinte arcering voor de gedempte `ziek`-weergave (Story 1.2). */
export const ZIEK_STRIPE_LIGHT = '#EEF1F4';
export const ZIEK_STRIPE_DARK = '#D6DEE5';
/** Gedempt (niet massief-navy, niet wit) tekst-/icoonkleur voor `ziek`. */
export const ZIEK_TEXT = '#64748B';

/**
 * Nadruk-tokens "werk voorgrond, afwezig achtergrond" (Epic
 * ux-aanwezigheidscel-duur, Story 1.2 — CAP-2). Statische, vooraf
 * berekende hex-tinten — GEEN `color-mix()` runtime-afhankelijkheid en
 * GEEN Tailwind arbitrary-class (adversariële review, bevinding 6: een
 * typfout in een arbitrary-class is een stille no-op, deze constanten
 * worden altijd via inline `style` toegepast).
 */
export const KANTOOR_BG = '#E9EEF2'; // ≈ navy #2F5A7A 15% op wit
export const KANTOOR_ACCENT = '#2F5A7A';
export const KANTOOR_TEXT = '#2F5A7A';
export const THUIS_BG = '#DDE9EB'; // ≈ teal #54949E 20% op wit
export const THUIS_ACCENT = '#54949E';
export const THUIS_TEXT = '#3d6b73';

/**
 * Standaard werktijden (Epic ux-aanwezigheidscel-duur, Story 1.2 — CAP-3,
 * Open Decision 4). Eén configureerbare bron — geen tijd-literalen
 * verspreid door componenten. Aangenomen `STANDARD_START='08:30'`/
 * `STANDARD_END='17:00'` tenzij anders vastgesteld.
 */
export const STANDARD_START = '08:30';
export const STANDARD_END = '17:00';

function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Whether a `werkt`-dagdeel matches the standard full-day span exactly —
 * a standard day shows no tijd-badge/duur-balk (CAP-3, "rust in de
 * standaard").
 */
export function isStandardDay(entry: { startTime?: string | null; endTime?: string | null }): boolean {
  return entry.startTime === STANDARD_START && entry.endTime === STANDARD_END;
}

/**
 * Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-1): whether a dagdeel's
 * span deviates from the standard span in ANY direction — shorter
 * (`dagdeelTimeLabel` returns text) OR longer (`dagdeelTimeLabel`
 * deliberately returns `null`, AC 15, but the duur-balk still renders,
 * clamped/full). This REPLACES the old `isVerkorteDag` (which only covered
 * the "shorter" direction and would have missed the "longer than standard"
 * overflow case entirely) as the single gate for BOTH the tijd-badge/
 * duur-balk visibility AND the aria-label's tijden-fragment — a screen-
 * reader user must get the real times whenever the sighted view shows a
 * non-standard duur-balk, even in the label-less overflow case.
 */
export function hasNonStandardSpan(entry: { startTime?: string | null; endTime?: string | null }): boolean {
  if (!entry.startTime || !entry.endTime) return false;
  return !isStandardDay(entry);
}

/**
 * `clamp01((eind − begin) / (STANDARD_END − STANDARD_START))` — tijden als
 * minuten-sinds-middernacht, GEEN lunchaftrek (roostertijden zijn
 * bruto-spannen, tech-spec CAP-3 / review-bevinding 4). `08:30–15:00` →
 * `390/510 ≈ 0,76`. Kept for backward compatibility (existing tests) —
 * superseded by `durationBar` below for the actual duur-balk rendering
 * (review-bevinding 7's left+width≤1 guarantee needs the combined
 * left/right-clamp `durationBar` computes, not this width-only ratio).
 */
export function durationRatio(startTime: string, endTime: string): number {
  const standardMinutes = timeToMinutes(STANDARD_END) - timeToMinutes(STANDARD_START);
  if (standardMinutes <= 0) return 0;
  return clamp01((timeToMinutes(endTime) - timeToMinutes(startTime)) / standardMinutes);
}

export interface DurationBar {
  /** Fraction of the standard span (0..1) where the duur-balk starts. */
  left: number;
  /** Fraction of the standard span (0..1) the duur-balk spans. */
  width: number;
}

/**
 * Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-1, AC 15 / review-
 * bevinding 7): offset (`left`) + breedte (`width`) for the duur-balk,
 * mapping `[startTime, endTime]` onto `[STANDARD_START, STANDARD_END]`.
 * Deliberately NOT `left = clamp01(rawLeft)` + `width =
 * clamp01(durationRatio(...))` computed independently — that combination
 * CAN overflow `left + width > 1` (e.g. a late start of 10:00 combined with
 * a very late end of 19:00: `rawLeft ≈ 0.18`, but `durationRatio` computes
 * width from the RAW (unclamped-relative-to-left) span, clamps it to 1
 * independently, and `0.18 + 1 > 1`). Instead, `left` and the clamped
 * RIGHT edge are each clamped to `[0,1]` independently, and `width` is
 * derived as `right - left` — this guarantees `left + width === right ≤ 1`
 * by construction, for every input, including someone starting before
 * `STANDARD_START` and/or working past `STANDARD_END` ("meer dan
 * standaard gewerkt" — the balk shows full/clamped, no overflow, matching
 * `dagdeelTimeLabel`'s deliberate "no label" for that same case).
 */
/**
 * Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (Taak 2): the shared
 * "clamp each edge independently, derive width from the clamped edges" form
 * — extracted out of `durationBar` so `kortVerlofPositionBar` below can
 * reuse the exact same left+width≤1 guarantee against a DIFFERENT span (the
 * employee's actual roosterspan, not the standard-dagspanne). Hergebruik van
 * de vorm, niet van de constanten (story dev-notes, Taak 2).
 */
function positionBar(spanStart: string, spanEnd: string, segmentStart: string, segmentEnd: string): DurationBar {
  const spanStartMinutes = timeToMinutes(spanStart);
  const spanMinutes = timeToMinutes(spanEnd) - spanStartMinutes;
  if (spanMinutes <= 0) return { left: 0, width: 0 };

  const rawLeft = (timeToMinutes(segmentStart) - spanStartMinutes) / spanMinutes;
  const rawRight = (timeToMinutes(segmentEnd) - spanStartMinutes) / spanMinutes;
  const left = clamp01(rawLeft);
  const right = clamp01(rawRight);
  return { left, width: Math.max(0, right - left) };
}

export function durationBar(startTime: string, endTime: string): DurationBar {
  return positionBar(STANDARD_START, STANDARD_END, startTime, endTime);
}

/**
 * Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (Taak 2, AC-1/AC-7):
 * offset (`left`) + breedte (`width`) for the kort-verlof tijdvak-segment,
 * mapping `[kortVerlofStart, kortVerlofEind]` onto `[rosterStart, rosterEnd]`
 * — the medewerker's WERKELIJKE roosterspan for that day, NOT the standard
 * dagspanne `durationBar` uses (UX-spec regel 136: `(begintijd − roosterstart)
 * / roosterduur`). Same left+width≤1 guarantee as `durationBar`, via the
 * shared `positionBar` form.
 */
export function kortVerlofPositionBar(
  rosterStart: string,
  rosterEnd: string,
  kortVerlofStart: string,
  kortVerlofEind: string
): DurationBar {
  return positionBar(rosterStart, rosterEnd, kortVerlofStart, kortVerlofEind);
}

/**
 * Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-1, AC 11-15, review-
 * bevinding 12): the SINGLE tijd-badge label helper — REPLACES
 * `shortEndLabel` entirely (AC 18, no two label-helpers side by side).
 * - Exact standard span → `null` (rust in de standaard).
 * - Starts before `STANDARD_START` OR ends after `STANDARD_END` ("meer dan
 *   standaard gewerkt") → `null` too (AC 15 — never claim "korter" for a
 *   day that's actually LONGER; the duur-balk alone communicates this via
 *   `durationBar`, clamped/full, no text).
 * - Only a later start (still ≤ `STANDARD_END`) → `"vanaf HH:MM"`.
 * - Only an earlier end (still ≥ `STANDARD_START`) → `"tot HH:MM"`.
 * - Both → `"van HH:MM tot HH:MM"`.
 */
export function dagdeelTimeLabel(startTime: string, endTime: string): string | null {
  if (startTime < STANDARD_START || endTime > STANDARD_END) return null;

  const startsLate = startTime > STANDARD_START;
  const endsEarly = endTime < STANDARD_END;

  if (startsLate && endsEarly) return `van ${startTime} tot ${endTime}`;
  if (endsEarly) return `tot ${endTime}`;
  if (startsLate) return `vanaf ${startTime}`;
  return null;
}

/**
 * Epic kort-verlof-duur, Story 1.1 (CAP-2): the roster work-span in hours,
 * derived from a `werkt`-dagdeel's `startTime`/`endTime` — the same "HH:MM"
 * fields already carried by the payload (Epic ux-aanwezigheidscel-duur,
 * Story 1.1). `null` when either time is missing or the resulting span
 * isn't positive (mirrors the backend's `computeRosterHours` guard, tech-spec
 * CAP-1 review-bevinding 2) — the caller then skips the kort-verlof
 * annotation entirely rather than dividing by zero.
 */
export function rosterHoursFromSpan(startTime?: string | null, endTime?: string | null): number | null {
  if (!startTime || !endTime) return null;
  const minutes = timeToMinutes(endTime) - timeToMinutes(startTime);
  return Number.isFinite(minutes) && minutes > 0 ? minutes / 60 : null;
}

export interface WerkVrijSplit {
  /** Fraction (0..1) of `rosterHours` that stays the gewerkt-segment. */
  werkFractie: number;
  /** Fraction (0..1) of `rosterHours` that is the kort-verlof-segment. */
  vrijFractie: number;
}

/**
 * Epic kort-verlof-duur, Story 1.1 (CAP-2, UX-spec "De weergave: gedeelde
 * duur-balk"): splits `rosterHours` into a gewerkt-fractie and a
 * kort-verlof-fractie for the gestapelde 2px-balk. Both fractions are
 * independently clamped to `[0,1]` and their sum never exceeds `1` (same
 * "clamp each edge independently, derive the rest" discipline as
 * `durationBar` above — review-bevinding 2's guard: an invalid/zero
 * `rosterHours` returns an all-zero split rather than `NaN`/`Infinity`).
 */
export function werkVrijSplit(rosterHours: number, kortVerlofUren: number): WerkVrijSplit {
  if (!Number.isFinite(rosterHours) || rosterHours <= 0) return { werkFractie: 0, vrijFractie: 0 };
  const vrijFractie = clamp01(kortVerlofUren / rosterHours);
  const werkFractie = clamp01(1 - vrijFractie);
  return { werkFractie, vrijFractie };
}

/**
 * Epic kort-verlof-duur, Story 1.1 (CAP-2, review-bevinding 7): NL-getal-
 * formattering gedeeld door `kortVerlofLabel`/`kortVerlofAriaFragment` —
 * hele getallen zonder decimaal (`1`), één decimaal met NL-komma anders
 * (`1,5`). Afgerond op 1 decimaal (Nmbrs `hours` kan bv. `21,19` bevatten).
 */
function formatNlUren(uren: number): string {
  const rounded = Math.round(uren * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
}

/**
 * Epic kort-verlof-duur, Story 1.1 (CAP-2, UX-spec "Microcopy"): het
 * zichtbare `Xu vrij`-badge-label — `"1u vrij"` / `"1,5u vrij"`.
 */
export function kortVerlofLabel(uren: number): string {
  return `${formatNlUren(uren)}u vrij`;
}

/**
 * Epic kort-verlof-duur, Story 1.1 (CAP-2, UX-spec "Microcopy", AC 1): het
 * aria-label-fragment — `"1 uur kort verlof"` / `"1,5 uur kort verlof"`.
 * Losstaand van `kortVerlofLabel` (ander format: "uur" voluit, geen "vrij")
 * zodat de aria-tekst leesbaar blijft voor een screenreader.
 */
export function kortVerlofAriaFragment(uren: number): string {
  return `${formatNlUren(uren)} uur kort verlof`;
}

/**
 * Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (Taak 3, AC-2/AC-12):
 * "HH:MM" → een compacte kloktijd voor het tijdvak-badge — hele uren zonder
 * `:00` (`"09:00"` → `"9"`), elke andere waarde mét minuten (`"09:30"` →
 * `"9:30"`). Deliberately NOT `formatNlUren` — dat formatteert een
 * uren-DUUR (`"1,5"`), geen kloktijd (`"9:30"`).
 */
function formatClockLabel(hhmm: string): string {
  const [hours, minutes] = hhmm.split(':');
  const hourNumber = Number(hours);
  return minutes === '00' ? String(hourNumber) : `${hourNumber}:${minutes}`;
}

/**
 * Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (Taak 3, AC-2/AC-12,
 * UX-spec regels 141-143): het zichtbare tijdvak-badge — `"9–11u"` /
 * `"9:30–11u"` / `"11:30–13:30u"`. Naast `kortVerlofLabel` (dat een DUUR
 * toont, `"1u vrij"`) voor het pad waar de tijden wél bekend zijn.
 */
export function kortVerlofTijdvakLabel(startTime: string, endTime: string): string {
  return `${formatClockLabel(startTime)}–${formatClockLabel(endTime)}u`;
}

/**
 * Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (Taak 3, AC-5/AC-12,
 * UX-spec regel 512): het voorleesfragment voor een tijdvak — voluit, met
 * leidende nul (`"kort verlof van 09:00 tot 11:00"`), zoals de UX-spec het
 * schrijft. Naast `kortVerlofAriaFragment` (dat alleen de DUUR voorleest)
 * voor het pad waar de tijden wél bekend zijn.
 */
export function kortVerlofTijdvakAriaFragment(startTime: string, endTime: string): string {
  return `kort verlof van ${startTime} tot ${endTime}`;
}

function hexToRgbChannels(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
}

function channelToHex(value: number): string {
  const byte = Math.round(Math.min(255, Math.max(0, value)));
  return byte.toString(16).padStart(2, '0');
}

/**
 * Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-3, AC 8): deterministic
 * JS hex-white-mix — NO `color-mix()` runtime dependency. `amount` is the
 * fraction of the ORIGINAL hue retained (e.g. `0.2` = 20% hue / 80% wit) —
 * matches the existing `KANTOOR_BG`/`THUIS_BG` precedent's "≈15-20% op wit"
 * convention (tech-spec CAP-3).
 */
export function pastelTint(hex: string, amount: number): string {
  const [r, g, b] = hexToRgbChannels(hex);
  const mix = (channel: number) => channel * amount + 255 * (1 - amount);
  return `#${channelToHex(mix(r))}${channelToHex(mix(g))}${channelToHex(mix(b))}`;
}

/**
 * Default retained-hue fraction for the verlof-pastel background (tech-spec
 * CAP-3: "~15-20% over wit gemengd"). A single constant so every verloftype
 * + the generic peer-kleur gets a visually consistent pastel strength.
 */
export const LEAVE_PASTEL_AMOUNT = 0.2;

/**
 * Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-3, AC 4 review-bevinding
 * / AC 9): a FIXED dark ink — deliberately NOT a lighter/darker variant
 * derived from the input hue (which would fail AA on an already-light hue
 * like a hypothetical yellow verloftype, per review-bevinding 4's exact
 * concern). Every `LEAVE_TYPE_COLORS`/`GENERIC_LEAVE_COLOR` pastel
 * (produced by `pastelTint` at `LEAVE_PASTEL_AMOUNT`) is light enough that
 * this single slate ink clears WCAG AA (≥4.5:1) with wide margin — verified
 * exhaustively by `aanwezigheidVisuals.pastel.test.ts`'s AC 9 test, which
 * fails the build if a future color addition ever regresses that. `hex` is
 * accepted (not just ignored silently) to keep the call-site symmetrical
 * with `pastelTint(hex, ...)` and to leave room for a future hue-aware
 * implementation without a signature change — today it is intentionally
 * unused.
 */
export function readableInk(_hex: string): string {
  return '#334155';
}

/**
 * Epic aanwezigheid-verlof-vrij, Story 1.1 (CAP-1, AC 1/AC 2): retourneert
 * voortaan ALTIJD "Vrij" — ongeacht `leaveType`, ongeacht of de kijker het
 * concrete verloftype mag zien (`canSeeLeaveDetail`). De kalender
 * beantwoordt "wie werkt er?"; het specifieke verloftype is daar ruis (zie
 * tech-spec "Why"). `leaveNameFor` is aanwezigheid-lokaal — dit raakt het
 * aanvraagformulier, de Nmbrs-verlofkalender en de gedeelde
 * `LEAVE_TYPE_NAMES` niet (die blijven "Wettelijk verlof" etc. tonen).
 */
export function leaveNameFor(_leaveType: number | null | undefined): string {
  return 'Vrij';
}

/**
 * Short, accessible text label per status — always shown alongside
 * color/pattern/icon (UX Cross-cutting, kleurenblind-veilig).
 *
 * Exhaustive `switch` (Story 1.3 code-review finding — consistency with
 * `AanwezigheidCell.HalfCell`'s exhaustiveness discipline, adversarial
 * review): `statusLabel` feeds `AanwezigheidCell`'s `aria-label` directly,
 * computed in the SAME render as `HalfCell` — before this fix, an
 * unrecognized status would have silently leaked a raw internal string
 * into the accessible name (e.g. `"Wilma Jansen, onbekende_status"`)
 * while `HalfCell` failed loudly for the identical entry one line later.
 * Both now fail the same way; the throw is safe to add because
 * `CellErrorBoundary` (added in the same fix) contains it to one grid
 * cell instead of crashing the whole attendance table.
 *
 * Epic aanwezigheidscel-iteratie2, Story 1.1 (CAP-4, AC 5, review-bevinding
 * 1): `verlof_in_afwachting` MUST stay its own explicit `case` — removing
 * it would break the `never`-exhaustiveness check below, since the status
 * remains in the shared `AttendanceDagdeelStatus` union — but it now falls
 * through to the SAME return as `werkt` (defensive type-backstop only; the
 * kalender-resolver never produces this status anymore).
 *
 * Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-2, AC 4/AC 12, review-
 * bevinding 10): `niet_werkbaar_patroon`'s text changed from
 * `"Vrij (rooster)"` to `"Niet ingeroosterd"` — clearer and consistent
 * with the new leeg-render (CAP-2): a fully empty day now carries the
 * aria-label "…, niet ingeroosterd" instead of the old "…, vrij (rooster)".
 *
 * Epic aanwezigheid-verlof-vrij, Story 1.1 (CAP-1, AC 5, review-bevinding
 * 7): `verlof_goedgekeurd`'s text changed from `"Verlof"` to `"Vrij"` —
 * this is what feeds `dagdeelAriaFragment`'s aria-label fragment for a
 * verlof-dagdeel, so the accessible name now matches the uniform visible
 * label from `leaveNameFor` (both say "Vrij", never the type-naam).
 */
export function statusLabel(status: AttendanceDagdeelStatus): string {
  switch (status) {
    case 'verlof_in_afwachting':
    case 'werkt':
      return 'Werkt';
    case 'niet_werkbaar_patroon':
      return 'Niet ingeroosterd';
    case 'verlof_goedgekeurd':
      return 'Vrij';
    case 'ziek':
      return 'Ziek';
    case 'feestdag':
      return 'Feestdag';
    case 'weekend':
      return 'Weekend';
    default: {
      const _exhaustiveCheck: never = status;
      throw new Error(`statusLabel: onbekende dagdeel-status "${String(_exhaustiveCheck)}"`);
    }
  }
}
